"use client";

/**
 * useSliderPages
 * @description 가로줄이 들고 있는 커서 페이지들과 다음 페이지 이어 받기. 첫 페이지는 서버 렌더가
 *   주고, 그 뒤는 `loadPage` (서버 액션) 로 받아 뒤에 붙인다.
 *
 *   `loadMore` 는 `Slider` 의 `onEndReached` 에 물린다 — 끝까지 밀면 다음 장이 붙는 방식이라
 *   더 받을 것이 없으면 `undefined` 를 돌려주어 보초 자체를 세우지 않는다.
 *
 *   한 번 실패하면 더 부르지 않는다. 가로줄은 화면을 채우는 곁가지라, 끝에 닿을 때마다 실패한
 *   요청을 되풀이하느니 지금까지 받은 것만 보여 주는 편이 낫다.
 */
import { useCallback, useMemo, useRef, useState } from "react";

interface CursorPage<T> {
  data: T[];
  nextCursor?: string;
}

export function useSliderPages<T>(
  firstPage: CursorPage<T>,
  loadPage: (cursor: string) => Promise<CursorPage<T>>,
): { items: T[]; loadMore?: () => void } {
  const [pages, setPages] = useState<CursorPage<T>[]>([firstPage]);
  const busyRef = useRef(false);
  const failedRef = useRef(false);

  const items = useMemo(() => pages.flatMap((page) => page.data), [pages]);
  const nextCursor = pages.at(-1)?.nextCursor;

  const loadMore = useCallback(async () => {
    if (busyRef.current || failedRef.current || !nextCursor) return;
    busyRef.current = true;
    try {
      const page = await loadPage(nextCursor);
      setPages((previous) => [...previous, page]);
    } catch (error) {
      failedRef.current = true;
      console.error("[useSliderPages] failed to load next page", error);
    } finally {
      busyRef.current = false;
    }
  }, [loadPage, nextCursor]);

  return { items, loadMore: nextCursor ? loadMore : undefined };
}
